



















































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import Draggable from 'vuedraggable';
import shortid from 'shortid';
import { PathViewModel, StateViewModel, Path } from '@/features/bots/models/designer';
import TextComponent from './TextComponent.vue';
import InputStringComponent from './InputStringComponent.vue';
import QuickReplyComponent from './QuickReplyComponent.vue';
import HumanHandoverComponent from './HumanHandoverComponent.vue';
import AppIntegrationComponent from './AppIntegrationComponent.vue';
import DecisionComponent from './DecisionComponent.vue';
import GoToComponent from './GoToComponent.vue';
import TriggerBox from './TriggerBox.vue';
import LinkedPathsBox from './LinkedPathsBox.vue';
import EditableText from '@/shared/components/EditableText.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import { DELETE_PATH } from '@/store/bot-designer/paths/actionTypes';
import { UPDATE_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { ADD_STATE, UPDATE_STATE } from '@/store/bot-designer/states/mutationTypes';
import { DELETE_STATE } from '@/store/bot-designer/states/actionTypes';
import { Mutation, Getter, Action } from 'vuex-class';
import { mixin as clickaway } from 'vue-clickaway';
import Modal from '@/shared/components/Modal.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import QuestionIcon from 'vue-material-design-icons/ForumOutline.vue';
import QuickReplyIcon from 'vue-material-design-icons/PlaylistCheck.vue';
import DecisionIcon from 'vue-material-design-icons/SourceBranch.vue';
import PauseBotIcon from 'vue-material-design-icons/Pause.vue';
import TextIcon from 'vue-material-design-icons/FormatTitle.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import GoToIcon from '@/shared/icons/GoToIcon.vue';
import { BusinessHoursViewModel } from '../../models';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';

enum DeleteModalType {
	State,
	Path
}

interface DeleteModalOptions {
	open: boolean;
	type: DeleteModalType;
	title: string;
	confirmation: string;
	confirm: () => void;
	cancel: () => void;
}

@Component({
	components: {
		DropDownMenu,
		Draggable,
		TextComponent,
		InputStringComponent,
		QuickReplyComponent,
		HumanHandoverComponent,
		AppIntegrationComponent,
		DecisionComponent,
		GoToComponent,
		EditableText,
		ConfirmationModal,
		Modal,
		TriggerBox,
		LinkedPathsBox,
		ErrorIcon,
		QuestionIcon,
		QuickReplyIcon,
		PauseBotIcon,
		TextIcon,
		DeleteIcon,
		DecisionIcon,
		GoToIcon
	},
	mixins: [
		clickaway,
	],

})
export default class PathDesigner extends Vue {
	@Getter public currentPath!: Path;
	@Getter public firstPath!: PathViewModel;
	@Getter public linkedPaths!: PathViewModel[];
	@Getter public trigger!: string;
	@Getter public triggerConditions!: ConditionGroupViewModel;
	@Getter public audienceConditions!: ConditionGroupViewModel;
	@Getter public scheduleCondition!: ConditionViewModel;
	@Getter public targetContactTypes!: string[];
	@Getter public openingTimes!: BusinessHoursViewModel[];

	@Action(DELETE_PATH) public deletePath: (pathId: string) => void;
	@Mutation(UPDATE_PATH) public updatePath: (path: PathViewModel) => void;
	@Mutation(ADD_STATE) public addState: (stateToAdd: StateViewModel) => void;
	@Mutation(UPDATE_STATE) public updateState: (updatedState: StateViewModel) => void;
	@Action(DELETE_STATE) public removeState: (stateToRemove: StateViewModel) => void;

	public isStatePickerOpen = false;
	public isStatePickerUp = false;
	public DeleteModalType = DeleteModalType;

	public $refs: {
		states: [TextComponent | InputStringComponent | QuickReplyComponent];
	};

	public deleteModalOptions: DeleteModalOptions = {
		open: false,
		type: DeleteModalType.State,
		title: '',
		confirmation: '',
		confirm: () => { return; },
		cancel: () => { return; },
	};

	public clearTransform(event: any) {
		// this is a fix for the modal within a state not appearing correctly after the state is dragged
		// the modal is position:fixed which can be relative to a parent that has a transform style, which we don't want.
		// see https://github.com/SortableJS/Sortable/issues/1687
		event.item.style.transform = null;
	}

	public toggleStatePicker() {
		this.isStatePickerOpen = !this.isStatePickerOpen;
	}


	@Watch('currentPath', {immediate: true, deep: true})
	public onValueChanged(value: Path, previous: Path) {
		if (previous && value.id === previous.id) {
			this.updatePath(value);
		}
	}


	public closeStatePicker() {
		this.isStatePickerOpen = false;
	}

	public openDeletePathModal() {
		this.deleteModalOptions = {
			open: true,
			type: DeleteModalType.Path,
			title: 'Delete Path',
			confirmation: '',
			confirm: this.deletePathConfirmed,
			cancel: this.closeDeleteModal
		};
	}

	public openDeleteStateModal(state: StateViewModel) {
		this.deleteModalOptions = {
			open: true,
			type: DeleteModalType.State,
			title: 'Delete State',
			confirmation: `Are you sure you want to delete the state?`,
			confirm: () => {
				this.deleteStateConfirmed(state);
			},
			cancel: this.closeDeleteModal
		};
	}

	public closeDeleteModal() {
		this.deleteModalOptions.open = false;
	}

	public deletePathConfirmed() {
		this.deletePath(this.currentPath.id);
		this.closeDeleteModal();
	}

	public deleteStateConfirmed(state: StateViewModel) {
		this.removeState(state);
		this.closeDeleteModal();
	}

	public getStateComponentName(state: StateViewModel) {
		if (state.component === 'InputString' && state.options  && state.options.length > 0) {
			return 'QuickReplyComponent';
		}

		return `${state.component}Component`;
	}

	public isFirstPathInFlow() {
		return this.currentPath.id === this.firstPath.id;
	}

	public addMessageState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'Text',
			text: '',
			invalid: true
		});
		this.focusOnStateInput();
	}

	public addQuestionState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'InputString',
			text: '',
			invalid: true
		});
		this.focusOnStateInput();
	}

	public addDecisionState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'Decision',
			invalid: true,
			decisions: [
				{
					transition: {
						type: undefined,
						payload: undefined
					},
					conditions: {
						type: 'and',
						conditions: []
					},
				}
			],
			noMatch: {
				payload: undefined,
				type: undefined
			}
		});
	}

	public addAppIntegrationState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'AppIntegration',
		});
	}

	public addQuickReplyState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'InputString',
			text: '',
			allowedAttempts: 1,
			options: [
				{ id: '1', text: '' },
			],
			invalid: true
		});
		this.focusOnStateInput();
	}

	public addHumanHandoverState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'HumanHandover'
		});
	}

	public addGoToState() {
		this.addState({
			id: `state-${shortid.generate()}`,
			component: 'GoTo',
			transition: {
				type: undefined,
				payload: undefined
			},
			invalid: true
		});
	}

	public stateUpdated(state: StateViewModel) {
		this.updateState(state);
	}

	public hasEndingStates() {
		return this.currentPath.hasEndingState();
	}

	public pathNameUpdated(text: string) {
		this.currentPath.name = text;
		this.updatePath(this.currentPath);
	}

	public setIsStatePickerUp(val: boolean) {
		this.isStatePickerUp = val;
	}

	private focusOnStateInput() {
		this.$nextTick(() => {
			const state = this.getLastAddedState();
			state?.focusOnInput();
		});
	}

	private getLastAddedState() {
		if (this.$refs.states && this.$refs.states.length > 0) {
			return this.$refs.states[this.$refs.states.length - 1];
		}
		return null;
	}
}
